/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../../../../../../core/components';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DropdownModel } from '../../../../../../../../core/models';
import { L7HeaderModel } from '../../../../../models/l7.header.model';
import { UtilsService } from '../../../../../../../../core/services';
import { hostnameValidation } from '../../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';
import { httpHeaderValidation } from '../../../../../directives';

@Component({
    templateUrl: './addEditHeader.component.html',
    styleUrls: ['addEditHeader.component.scss']
})

export class AddEditHeaderComponent extends BaseComponent implements OnInit {
    /**
     * New headerForm formGroup.
     */
    public headerForm: FormGroup;

    /**
     * Header details.
     */
    public header: any;

    /**
     * Store the required dropdown content.
     */
    public dropdown = {
        requestTypes: [
            <DropdownModel>{
                key: 'request',
                value: 'Request'
            },
            <DropdownModel>{
                key: 'response',
                value: 'Response'
            },
        ],
        options: [
            <DropdownModel>{
                key: 'add',
                value: 'Add'
            },
            <DropdownModel>{
                key: 'set',
                value: 'Set'
            },
            <DropdownModel>{
                key: 'delete',
                value: 'Delete'
            },
            <DropdownModel>{
                key: 'replace',
                value: 'Replace'
            }
        ]
    };

    /**
     * Call back function.
     * @private
     */
    private callBack: (...args: any[]) => void;

    /**
     * Store initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * List of all headers for this service, to check for duplicates against.
     */
    public headers: L7HeaderModel[] = [];

    /**
     * Stores filtered for checking for duplicates.
     * @private
     */
    private filteredHeaders: L7HeaderModel[] = [];

    constructor(
        private cdr: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpFormGroup();
        this.setUpFormSubscribers();
    }

    public ngOnInit(): void {
        this.setUpFilteredHeaders();
        if (this.header) {
            this.patchToForm(this.header);
            this.cdr.detectChanges();
        }
    }

    /**
     * Submit form data to call back.
     */
    public submitHeaderToCallBack(): void {
        const formValues: any = this.getFormValues();

        this.callBack(formValues);
    }

    /**
     * Get form values.
     * @private
     */
    private getFormValues(): any {
        const formValues: any = this.headerForm.value;
        if (!formValues?.id) {
            formValues.id = 'temp-' + Date.now() + Math.random();
        }

        return formValues;
    }

    /**
     * Set up the form group for new headers.
     * @private
     * */
    private setUpFormGroup(): void {
        this.headerForm = this.formBuilder.group({
            id: [''],
            type: [null, Validators.required],
            option: [null, Validators.required],
            name: [{ value: '', disabled: true }, [hostnameValidation, Validators.required]],
            value: [{ value: '', disabled: true }, [httpHeaderValidation, Validators.maxLength(512)]],
            replace: [{ value: '', disabled: true }, [httpHeaderValidation, Validators.maxLength(512)]]
        });

        this.initialFormData = this.headerForm.value;
    }

    /**
     * Cancel the add / edit header form.
     */
    public cancelAddEditHeader(): void {
        this.callBack(null);
    }

    /**
     * Setup form subscriber.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.headerForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any) => {
                    this.utilsService.debounce('value', (): void => {
                        this.checkForDuplicates(values);

                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, values)) {
                            this.headerForm.markAsPristine();
                        } else {
                            this.headerForm.markAsDirty();
                        }
                    }, 500);
                }
            });

        this.headerForm.get('option').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: option => {
                    this.showHideInputsBasedOnOption(option);
                }
            });
    }

    /**
     * Check for duplicate headers.
     * @param values
     * @private
     */
    private checkForDuplicates(values: L7HeaderModel): void {
        const match: boolean = this.filteredHeaders.some((item: L7HeaderModel) => item.name === values.name && item.type === values.type);
        const nameField: AbstractControl = this.headerForm.get('name');
        const typeField: AbstractControl = this.headerForm.get('type');
        if (match) {
            const message = 'duplicate_header_name_type';
            nameField.setErrors({ 'nameMatch': message });
            typeField.setErrors({ 'typeMatch': message });
        } else {
            if (nameField.hasError('nameMatch')) {
                nameField.setErrors(null);
            }

            if (typeField.hasError('typeMatch')) {
                typeField.setErrors(null);
            }
        }
    }

    /**
     * Set up he
     * @private
     */
    private setUpFilteredHeaders(): void {
        const headersClone: L7HeaderModel[] = this.utilsService.duplicateObject(this.headers);
        if (this.header) {
            const deleteIndex: number = headersClone.findIndex((item: L7HeaderModel) => item.id === this.header.id);
            if (deleteIndex !== -1) {
                headersClone.splice(deleteIndex, 1);
            }
        }

        this.filteredHeaders = headersClone;
    }

    /**
     * Populate the row data to the active form.
     * @param row
     * @private
     */
    private patchToForm(row: any): void {
        this.headerForm.patchValue(row);
        this.initialFormData = this.headerForm.value;
    }

    /**
     * Validate the header value. Is 'delete' option is selected then the header value should be disabled
     * @param option
     * @private
     */
    private showHideInputsBasedOnOption(option: string): void {
        const nameField: AbstractControl = this.headerForm.get('name');
        const valueField: AbstractControl = this.headerForm.get('value');
        const replaceField: AbstractControl = this.headerForm.get('replace');

        if (option !== null) {
            nameField.enable();
        }
        if (option === 'add' || option === 'set') {
            this.enableValueField(valueField);
            replaceField.disable();
        }
        if (option === 'replace') {
            this.enableValueField(valueField);
            replaceField.setValidators([httpHeaderValidation, Validators.maxLength(512), Validators.required]);
            replaceField.enable();
        }
        if (option === 'delete') {
            valueField.disable();
            replaceField.disable();
        }
    }

    /**
     * Enable value field
     * @param valueField
     * @private
     */
    private enableValueField(valueField: AbstractControl<any, any>): void {
        valueField.enable();
        valueField.setValidators([httpHeaderValidation, Validators.maxLength(512), Validators.required]);
    }
}
